import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Search, MapPin, ExternalLink } from 'lucide-react';
import CategoryNav from './CategoryNav';
import { useManagedCollection, slugify } from "../admin/useManagedCollection";
import { AdminItem, AdminAddBar } from "../admin/AdminControls";
import type { FieldDef } from "../admin/AdminFieldModal";
import NewsTicker from './NewsTicker';
import SocialLinks, { SOCIAL_FIELDS } from './SocialLinks';

const STARTUP_FIELDS: FieldDef[] = [
  { key: "name", label: "Name", required: true },
  { key: "address", label: "Address", type: "textarea" },
  { key: "website", label: "Website URL", placeholder: "https://…" },
  { key: "facebook", label: "Facebook URL", placeholder: "https://facebook.com/…" },
  { key: "image", label: "Image URL", type: "image", placeholder: "https://…" },
  ...SOCIAL_FIELDS,
];

const SEED_STARTUPS: any[] = [];

const FALLBACK_IMAGE = 'https://images.unsplash.com/photo-1559136555-9303baea8ebd?w=600&h=400&fit=crop';

function mapsUrl(entry: { name: string; address?: string }) {
  return `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(`${entry.name}, ${entry.address || ''}`)}`;
}

function StartupCard({ entry, index }: { entry: { name: string; address?: string; website?: string; facebook?: string; image?: string }; index: number }) {
  return (
    <motion.div
      initial={{ opacity: 0, y: 16 }}
      animate={{ opacity: 1, y: 0 }}
      viewport={{ once: true, margin: '-80px' }}
      transition={{ duration: 0.5, delay: index * 0.08 }}
      className="bg-white rounded-lg border border-gray-100 shadow-sm hover:shadow-md transition-all duration-300 overflow-hidden flex flex-col group"
      style={{ fontFamily: "'Poppins', sans-serif" }}
    >
      <div className="relative w-full h-24 overflow-hidden bg-gray-50">
        <img
          src={entry.image || FALLBACK_IMAGE}
          alt={entry.name}
          className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
          loading="lazy"
          onError={(e) => {
            (e.target as HTMLImageElement).src = FALLBACK_IMAGE;
          }}
        />
      </div>
      <div className="flex flex-col flex-1 px-2 py-2 gap-1">
        <h3 className="font-semibold text-gray-900 leading-tight text-[11px] truncate">{entry.name}</h3>
        {entry.address && (
          <div className="flex items-start gap-0.5">
            <MapPin className="w-2.5 h-2.5 text-cyan-500 shrink-0 mt-0.5" />
            <span className="text-[10px] text-gray-500 leading-snug line-clamp-2">{entry.address}</span>
          </div>
        )}
        <div className="mt-auto pt-1 flex flex-col gap-1">
          <SocialLinks item={entry} px={20} />
          <a
            href={mapsUrl(entry)}
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center justify-center gap-0.5 font-semibold text-white bg-[#0891b2] hover:bg-[#0e7490] rounded-md transition-colors duration-200 px-2 py-1 text-[10px]"
          >
            <ExternalLink className="w-2.5 h-2.5" /> View Location
          </a>
        </div>
      </div>
    </motion.div>
  );
}

export default function StartUps() {
  const navigate = useNavigate();
  const [searchQuery, setSearchQuery] = useState('');

  const mc = useManagedCollection<any>("start_ups", SEED_STARTUPS, (a, i) => slugify(a.name || String(i)));

  const filtered = mc.items.filter((a) => {
    const q = searchQuery.trim().toLowerCase();
    if (!q) return true;
    return a.name.toLowerCase().includes(q) || (a.address || '').toLowerCase().includes(q);
  });

  // Respect the admin-managed order (drag-reorder) instead of alphabetical, so
  // editing a card doesn't move it and reordering persists on the public site.
  const sorted = [...filtered].sort((a: any, b: any) => (a._order ?? 0) - (b._order ?? 0));

  return (
    <div className="min-h-screen bg-white">
      {/* Hero */}
      <section className="relative w-full" style={{ minHeight: "160px" }}>
        <div className="absolute inset-0 overflow-hidden">
          <img
            src="https://images.unsplash.com/photo-1559136555-9303baea8ebd?w=1440&h=400&fit=crop"
            alt=""
            className="absolute inset-0 w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-black/45" />
          <svg viewBox="0 0 1440 24" preserveAspectRatio="none" style={{ position: 'absolute', left: 0, bottom: '-1px', width: '100%', height: '18px', display: 'block' }}>
            <path d="M0,0 Q720,24 1440,0 L1440,24 L0,24 Z" fill="#ffffff" />
          </svg>
        </div>
        <div className="relative max-w-[1320px] w-[calc(100%-40px)] mx-auto px-4 flex items-center justify-center" style={{ zIndex: 10, minHeight: "160px" }}>
          <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5, delay: 0.1 }} className="w-full max-w-[600px]">
            <div className="rounded-md" style={{ boxShadow: '0 0 40px 12px rgba(255,255,255,0.45), 0 0 80px 24px rgba(255,255,255,0.2)', backdropFilter: 'blur(4px)' }}>
              <div className="flex bg-white rounded-md shadow-md h-[40px]">
                <div className="flex-1 px-3 flex items-center min-h-[40px]">
                  <span className="text-[11px] text-gray-600 font-semibold shrink-0 mr-2 hidden sm:inline">Find startups</span>
                  <input
                    type="text"
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    placeholder="e.g. Tech Startup, SaaS"
                    className="w-full text-[12px] text-gray-700 placeholder:text-gray-400 outline-none bg-transparent"
                  />
                </div>
                <button className="bg-[#0891b2] hover:bg-[#0e7490] text-white text-[13px] font-bold px-5 transition-colors duration-200 shrink-0 min-h-[40px] rounded-r-md">
                  <span className="flex items-center justify-center gap-1.5">
                    <Search className="w-3.5 h-3.5 sm:hidden" />
                    Search
                  </span>
                </button>
              </div>
            </div>

          </motion.div>
        </div>
      </section>
      <NewsTicker />

      {/* Secondary Category Nav */}
      <CategoryNav />

      {/* Listings */}
      <section className="bg-gray-50/50 py-8">
        <div className="max-w-[1320px] mx-auto px-4">
          <motion.div initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5, delay: 0.2 }}>
            <h2 className="text-[18px] sm:text-[20px] md:text-[22px] font-bold text-gray-900 mb-1" style={{ fontFamily: "'Poppins', sans-serif" }}>
              Start Ups in Dallas-DFW
            </h2>
            <p className="text-[12px] sm:text-[13px] text-gray-500 mb-6" style={{ fontFamily: "'Poppins', sans-serif" }}>
              Discover innovative startups and entrepreneurial ventures in the Dallas–Fort Worth area.
            </p>

            <AdminAddBar mc={mc} fields={STARTUP_FIELDS} label="Add Startup" />

            {sorted.length > 0 && (
              <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-2">
                {sorted.map((entry, index) => (
                  <AdminItem key={entry._key} mc={mc} item={entry} fields={STARTUP_FIELDS} title="Startup">
                    <StartupCard entry={entry} index={index} />
                  </AdminItem>
                ))}
              </div>
            )}

            {sorted.length === 0 && (
              <div className="text-center py-12">
                <p className="text-gray-400 text-[14px]">No startup listings yet. Be the first to add one!</p>
              </div>
            )}
          </motion.div>
        </div>
      </section>
    </div>
  );
}
